import React, { useEffect, useRef, useState } from "react";
import { decamelizeKeys, camelizeKeys } from "humps";
import isEqual from "react-fast-compare";
import { useTranslation } from "react-i18next";
import { css } from "@emotion/react";
import { unwrapResult } from "@reduxjs/toolkit";
import { useForm, FormProvider, Resolver } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { accentColor } from "main/javascripts/styles/base/colorStyle";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  fontWeight,
  letterSpacing,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import * as yup from "yup";
import {
  FormNamespaces,
  yupDefaultMessages,
} from "main/javascripts/constants/FormConstants";
import { useAppDispatch, useAppSelector } from "main/javascripts/store";
import { useRouter } from "next/router";
import { CruiseSchedulePickerBlock } from "./CruiseSchedulePickerBlock";
import {
  clearCruiseCabinRates,
  convertSearchCruiseCabinFormNumericValues,
  convertSearchCruiseCabinFormValuesToParams,
  cruiseSelector,
  hasRatesSelector,
  hasSaleRatesSelector,
  searchCruiseCabins,
  setCruiseSchedulePickerDisplayed,
} from "../cruiseSlice";
import {
  cruiseShowParamSelector,
  cruiseShowParamToSearchCabinParam,
  initCruiseShowParam,
} from "main/javascripts/slices/paramSlice";
import { updateCruiseShowUrl } from "main/javascripts/utils/cruiseUtil";
import { CruiseGuestPickerBlock } from "./CruiseGuestPickerBlock";
import { minResolution } from "main/javascripts/styles/base/responsiveStyle";
import { formatAndConvertServerErrorToForm } from "main/javascripts/utils/FormUtil";
import { DEFAULT_ADULTS } from "main/javascripts/constants/CruiseConstants";
import { FormErrorsBlock } from "main/javascripts/components/form/FormErrorsBlock";
import { borderRadius } from "main/javascripts/styles/base/borderStyle";
import { boxShadow } from "main/javascripts/styles/base/shadowStyle";
import { parseDate } from "../../../utils/DateUtil";
import { isBefore, isValid } from "date-fns";
import { loadingSelector } from "../cruiseSlice";
import { FormNoticesBlock } from "main/javascripts/components/form/FormNoticesBlock";

type CruiseCabinSearchFormData = {
  form: any; // エラー用
  departure_date: string;
  guests: any[];
};
const cruiseCabinSearchFormDataKeys: Array<keyof CruiseCabinSearchFormData> = [
  "departure_date",
  "guests",
];

yup.setLocale(yupDefaultMessages);
const schema = yup.object().shape({
  departure_date: yup.string().required(),
  guests: yup.array().of(
    yup.object().shape({
      number_of_children: yup
        .number()
        .nullable()
        .transform((_, val) => (val || val === 0 ? Number(val) : null))
        .required(),
      ages_of_children: yup.array().when("number_of_children", {
        is: (val) => Number(val) > 0,
        then: yup.array().of(
          yup.object().shape({
            age: yup
              .number()
              .nullable()
              .transform((_, val) => (val || val === 0 ? Number(val) : null))
              .required(),
          })
        ),
      }),
    })
  ),
});

export const CruiseCabinSearchForm = () => {
  const cruise = useAppSelector(cruiseSelector);
  const hasRates = useAppSelector(hasRatesSelector);
  const hasSaleRates = useAppSelector(hasSaleRatesSelector);
  const isLoading = useAppSelector(loadingSelector);
  const param = useAppSelector(cruiseShowParamSelector);
  // callback内でparamの最新の値が取得できないためrefでコピーを持つ
  const paramRef = useRef(param);
  const prevSubmitparams = useRef(null);
  const appDispatch = useAppDispatch();
  const { t } = useTranslation(["button", "component"]);

  const defaultValues = {
    departure_date: cruise.defaultSchedule?.departureDate,
    guests: [
      {
        number_of_adult: DEFAULT_ADULTS,
        number_of_children: 0,
      },
    ],
  };

  const localStorageGuestsKey = "cruiseCabinSearchFormGuests";

  const methods = useForm<CruiseCabinSearchFormData>({
    resolver: yupResolver(
      schema
    ) as unknown as Resolver<CruiseCabinSearchFormData>,
    defaultValues: {
      ...defaultValues,
      ...cruiseShowParamToSearchCabinParam(decamelizeKeys(param)),
    },
  });
  const {
    handleSubmit,
    getValues,
    watch,
    reset,
    setError,
    trigger,
    formState: { errors },
  } = methods;
  const router = useRouter();

  const values = watch();
  const prevParam = useRef(null);

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    // formの構造で比較している
    const newParam = cruiseShowParamToSearchCabinParam(param);
    const formParam = convertSearchCruiseCabinFormNumericValues(values);
    paramRef.current = param;
    if (
      !isEqual(newParam, formParam) &&
      !isEqual(prevParam.current, newParam)
    ) {
      // paramからフォームに必要な値だけ設定する
      const newFormValues = defaultValues;

      // localStorageに保存されたguestsをデフォルトにする
      const storedGuestsValues = localStorage.getItem(localStorageGuestsKey);
      if (storedGuestsValues) {
        const parsedValues = JSON.parse(storedGuestsValues);
        if (
          parsedValues &&
          parsedValues.expiry &&
          parsedValues.expiry > new Date().getTime()
        ) {
          newFormValues.guests = parsedValues.value;
        } else {
          localStorage.removeItem(localStorageGuestsKey);
        }
      }

      const decamelizedkeysNewParam = decamelizeKeys(newParam);
      cruiseCabinSearchFormDataKeys.map((key) => {
        if (decamelizedkeysNewParam[key]) {
          newFormValues[key] = decamelizedkeysNewParam[key];
        }
      });
      reset(newFormValues);

      if (isValidParam(newFormValues)) {
        onSubmit(newFormValues);
      }
    }
    prevParam.current = newParam;
  }, [param, router.isReady]);

  const isValidParam = (param) => {
    if (param.departure_date && param.guests) {
      const departureDate = parseDate(param.departure_date);
      if (isValid(departureDate)) {
        const today = new Date();
        if (isBefore(departureDate, today)) {
          setError("departure_date", {
            type: "string",
            message: t("label:form.errors.messages.beforeToday", {
              date: param.departure_date,
            }),
          });
          return false;
        }
        return true;
      }
      return false;
    }
  };

  const updateGuestsParamWithFormValue = (submit = false) => {
    const allValues = getValues();
    const formParams = convertSearchCruiseCabinFormValuesToParams(allValues);
    const guestsValue = formParams.guests;

    const decamelizedParamGuests = decamelizeKeys(
      paramRef.current?.guests || {}
    );

    if (!isEqual(decamelizedParamGuests, guestsValue)) {
      const newParam = {
        ...paramRef.current,
        ...camelizeKeys(formParams, function (key, convert) {
          return /^_.+$/.test(key) ? key : convert(key);
        }),
      };

      trigger().then((isValid) => {
        if (isValid) {
          appDispatch(initCruiseShowParam(newParam));
          updateCruiseShowUrl(decamelizeKeys(newParam), router);
          if (submit) {
            const submitParam = decamelizeKeys(allValues);
            if (isValidParam(submitParam)) {
              onSubmit(submitParam);
            }
          }
        }
      });
    }
  };

  const onGuestPickerHide = () => {
    updateGuestsParamWithFormValue(true);
  };

  const onSubmitError = (errors) => {
    if (errors.departure_date) {
      updateGuestsParamWithFormValue();
      appDispatch(setCruiseSchedulePickerDisplayed(true));
    }
  };

  const onSubmit = (data) => {
    if (!cruise.defaultSchedule?.departureDate) {
      return;
    }
    const submitParams = convertSearchCruiseCabinFormValuesToParams({
      ...data,
    });

    if (isEqual(prevSubmitparams.current, submitParams)) {
      // formに保存したエラーがsubmit時に消えてしまうので再設定する
      if (errors.form) {
        setError("form", errors.form);
      }
      return;
    }

    appDispatch(clearCruiseCabinRates());

    const expirationDate = new Date();
    expirationDate.setMonth(expirationDate.getMonth() + 1);
    const storageData = {
      value: data.guests,
      expiry: expirationDate.getTime(),
    };
    localStorage.setItem(localStorageGuestsKey, JSON.stringify(storageData));

    appDispatch(
      searchCruiseCabins({
        uniqueId: cruise.uniqueId,
        params: submitParams,
      })
    )
      .then(unwrapResult)
      .catch((errorResponse) => {
        formatAndConvertServerErrorToForm(errorResponse, setError);
      });

    const newParam = {
      ...paramRef.current,
      ...camelizeKeys(submitParams, function (key, convert) {
        return /^_.+$/.test(key) ? key : convert(key);
      }),
    };
    appDispatch(initCruiseShowParam(newParam));
    updateCruiseShowUrl(decamelizeKeys(newParam), router);
    prevSubmitparams.current = submitParams;
  };

  const innerStyles = isMounted ? innerStyle : [innerStyle, innerLoadingStyle];
  const formStyles = isLoading ? formDisabledStyle : null;

  const shouldShowNotice = !isLoading && hasRates;
  const notices = [t("component:cruise.finalPriceNoticeShort")];
  if (hasSaleRates) {
    notices.push(t("component:cruise.salePriceNoticeShort"));
  }

  return (
    <div css={blockStyle}>
      <div css={formAreaStyle}>
        <div css={innerStyles}>
          <FormProvider {...methods}>
            <form
              onSubmit={handleSubmit(onSubmit, onSubmitError)}
              css={formStyles}
            >
              <div css={formBlockStyle}>
                <CruiseSchedulePickerBlock
                  onUpdate={handleSubmit(onSubmit, onSubmitError)}
                />
                <CruiseGuestPickerBlock
                  onComplete={handleSubmit(onSubmit, onSubmitError)}
                  onHide={onGuestPickerHide}
                />
                <div css={buttonBlockStyle}>
                  <button type="submit" css={buttonStyle}>
                    {t("common.search")}
                  </button>
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
        {errors && (
          <div css={errorBlockStyle}>
            <div css={errorInnerStyle}>
              <FormErrorsBlock
                namespace={FormNamespaces.cruise}
                errors={errors}
                hideTitle={true}
              />
            </div>
          </div>
        )}
        {shouldShowNotice && (
          <div css={errorBlockStyle}>
            <div css={errorInnerStyle}>
              <FormNoticesBlock noticeMessages={notices} hideIcon={true} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const blockStyle = css``;
const formAreaStyle = css`
  padding: ${space.atom} 0;
  ${moreThanBreakpoint("tablet")} {
    padding: ${space.atom2x} 0;
  }
`;
const innerStyle = css`
  margin: 0;
  transition: opacity 0.4s ease;
  box-shadow: ${boxShadow.interface};
  ${moreThanBreakpoint("tablet")} {
    margin: 0;
  }
`;
const innerLoadingStyle = css`
  opacity: 0;
`;
const formBlockStyle = css`
  display: grid;
  grid-template-columns: minmax(140px, 30%) 1fr;
  position: relative;
  overflow: hidden;
  ${moreThanBreakpoint("tablet")} {
    overflow: visible;
    border-radius: 0;
    grid-template-columns: minmax(160px, 30%) 1fr 6rem;
  }
`;
const buttonBlockStyle = css`
  padding: 0;
  height: 100%;
  width: 100%;
  max-width: 6rem;
  display: none;
  ${moreThanBreakpoint("tablet")} {
    display: block;
  }
`;
const buttonStyle = css`
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
  outline: none;
  cursor: pointer;
  letter-spacing: ${letterSpacing.text};
  margin: 0;
  width: 100%;
  transition: background 0.4s ease, opacity 0.4s ease;
  display: flex;
  height: 3.5rem;
  line-height: ${lineHeight.body2};
  min-width: 6rem;
  align-items: center;
  justify-content: center;
  font-weight: ${fontWeight.regular};
  color: white;
  background-color: ${accentColor.primaryColor};
  border: none;
  border-top-right-radius: ${borderRadius.normal};
  border-bottom-right-radius: ${borderRadius.normal};
  &:hover {
    background-color: ${accentColor.primaryDarkColor};
  }
  &:disabled {
    opacity: 0.75;
    cursor: default;
    &:hover {
      background-color: ${accentColor.primaryColor};
    }
  }
  ${minResolution("scale2x")} {
    font-weight: ${fontWeight.medium};
  }
  ${moreThanBreakpoint("tablet")} {
    height: 4rem;
  }
`;
const errorBlockStyle = css``;
const errorInnerStyle = css`
  margin: 0 -${space.atom};
  & > div {
    margin-bottom: 0;
  }
}`;
const formDisabledStyle = css`
  pointer-events: none;
  filter: grayscale(1);
  opacity: 0.5;
`;
